import { AxiosInstance } from 'axios';
import { MaintenanceWindow, ReceiptAiIntegrationStatus, ReceiptAiMemory, ReceiptAiMemoryHistory, ReceiptAiMemoryMetrics, ReceiptAiMemoryStatus, ReceiptAiMemoryType, ReceiptAiMode, ReceiptAiCredentialSource, TimesheetFinancialRebuildPreview } from '../../types';

export function createSettingsApi(client: AxiosInstance) {
  return {
    async uploadAnnouncementImage(data: string) { return (await client.post<{ id: number }>('/settings/announcements/images', { data })).data; },
    async getAnnouncementImage(imageId: number, announcementId?: number) {
      const url = announcementId ? `/announcements/${announcementId}/images/${imageId}` : `/settings/announcements/images/${imageId}`;
      return (await client.get<Blob>(url, { responseType: 'blob', suppressAuthRedirect: true } as any)).data;
    },
    async listAnnouncements() { return (await client.get<import('../../types').Announcement[]>('/announcements', { suppressAuthRedirect: true } as any)).data; },
    async acknowledgeAnnouncement(id: number) { await client.post(`/announcements/${id}/seen`); },
    async listManagedAnnouncements() { return (await client.get<import('../../types').Announcement[]>('/settings/announcements')).data; },
    async saveAnnouncement(data: import('../../types').AnnouncementInput, id?: number) {
      return id ? (await client.put(`/settings/announcements/${id}`, data)).data : (await client.post('/settings/announcements', data)).data;
    },
    async withdrawAnnouncement(id: number) { await client.delete(`/settings/announcements/${id}`); },
    async getSettings() { const response = await client.get('/settings'); return response.data; },
    async getFieldReportSettings() { const response = await client.get('/settings/field-reports'); return response.data; },
    async getBranding() {
      // The logo comes back as a cacheable image URL instead of ~0.5 MB inline data.
      const response = await client.get('/settings/branding', {
        params: { logo: 'url' },
        suppressAuthRedirect: true,
      } as any);
      const data = response.data || {};
      return data.company_logo_path
        ? { ...data, company_logo: `${client.defaults.baseURL || ''}${data.company_logo_path}` }
        : data;
    },
    async updateSettings(data: Record<string, any>) {
      const response = await client.patch('/settings', data);
      return response.data;
    },
    async getDropboxIntegrationStatus() {
      const response = await client.get('/integrations/dropbox/status');
      return response.data as {
        configured: boolean;
        connected: boolean;
        integration: null | {
          id: number;
          provider: string;
          status: string;
          account_email?: string | null;
          account_name?: string | null;
          root_path?: string | null;
          connected_at?: string | null;
          updated_at?: string | null;
        };
      };
    },
    async startDropboxConnect() {
      const response = await client.post('/integrations/dropbox/connect');
      return response.data as { url: string };
    },
    async disconnectDropbox() {
      const response = await client.post('/integrations/dropbox/disconnect');
      return response.data;
    },
    async testDropboxUpload() {
      const response = await client.post('/integrations/dropbox/test-upload');
      return response.data as { ok: boolean; path: string; upload?: any };
    },
    async getTemplates() {
      const response = await client.get('/templates');
      return response.data;
    },
    async createTemplate(name: string, rows: any[], include_hours = false) {
      const response = await client.post('/templates', { name, rows, include_hours });
      return response.data;
    },
    async deleteTemplate(id: number) {
      const response = await client.delete(`/templates/${id}`);
      return response.data;
    },
    async getReceiptAiIntegrationStatus() {
      const response = await client.get('/integrations/receipt-ai/status');
      return response.data as ReceiptAiIntegrationStatus;
    },
    async connectReceiptAi(apiKey: string) {
      const response = await client.post('/integrations/receipt-ai/connect', { api_key: apiKey });
      return response.data as ReceiptAiIntegrationStatus;
    },
    async disconnectReceiptAi() {
      const response = await client.post('/integrations/receipt-ai/disconnect');
      return response.data as ReceiptAiIntegrationStatus;
    },
    async updateReceiptAiSettings(data: {
      enabled?: boolean;
      credential_source?: ReceiptAiCredentialSource;
      default_mode?: ReceiptAiMode;
      allow_per_receipt_mode?: boolean;
      automatic_escalation?: boolean;
      monthly_budget_cents?: number | null;
    }) {
      const response = await client.patch('/integrations/receipt-ai/settings', data);
      return response.data as ReceiptAiIntegrationStatus;
    },
    async testReceiptAiConnection() {
      const response = await client.post('/integrations/receipt-ai/test');
      return response.data as { ok: boolean };
    },
    async getReceiptAiMemories(filters: { status?: ReceiptAiMemoryStatus | ''; type?: ReceiptAiMemoryType | ''; search?: string } = {}) {
      const response = await client.get('/integrations/receipt-ai/memories', { params: filters });
      return response.data as { memories: ReceiptAiMemory[] };
    },
    async getReceiptAiMemoryMetrics(days = 90) {
      const response = await client.get('/integrations/receipt-ai/memories/metrics', { params: { days } });
      return response.data as ReceiptAiMemoryMetrics;
    },
    async getReceiptAiMemoryHistory(id: number) {
      const response = await client.get(`/integrations/receipt-ai/memories/${id}/audit`);
      return response.data as ReceiptAiMemoryHistory;
    },
    async updateReceiptAiMemory(id: number, data: { action?: 'approve' | 'reject' | 'quarantine' | 'retire' | 'restore' | 'edit'; learned_value?: Record<string, unknown>; reason?: string }) {
      const response = await client.patch(`/integrations/receipt-ai/memories/${id}`, data);
      return response.data as { memory: ReceiptAiMemory };
    },
    async getMaintenanceStatus() {
      const response = await client.get('/maintenance/status', {
        // This background poll must stay silent during a transient mobile outage.
        suppressAuthRedirect: true,
      } as any);
      return response.data as { status: 'none' | 'upcoming' | 'active'; window: MaintenanceWindow | null; manual: boolean };
    },
    async getManualMaintenanceStatus() {
      const response = await client.get('/maintenance/manual-status');
      return response.data as { active: boolean };
    },
    async setManualMaintenanceMode(active: boolean) {
      const response = await client.put('/maintenance/manual', { active });
      return response.data as { active: boolean };
    },
    async listMaintenanceWindows() {
      const response = await client.get('/admin/maintenance');
      return response.data as MaintenanceWindow[];
    },
    async createMaintenanceWindow(data: Partial<MaintenanceWindow>) {
      const response = await client.post('/admin/maintenance', data);
      return response.data as MaintenanceWindow;
    },
    async updateMaintenanceWindow(id: number, data: Partial<MaintenanceWindow>) {
      const response = await client.patch(`/admin/maintenance/${id}`, data);
      return response.data as MaintenanceWindow;
    },
    async deleteMaintenanceWindow(id: number) {
      await client.delete(`/admin/maintenance/${id}`);
    },
    async getTimesheetFinancialRebuildPreview(projectId?: number) {
      const response = await client.get('/maintenance/timesheet-financial-rebuild', {
        params: projectId ? { project_id: projectId } : undefined,
      });
      return response.data as TimesheetFinancialRebuildPreview;
    },
    async rebuildTimesheetFinancials(data: {
      project_id?: number | null;
      week_start_dates: string[];
      confirmation: 'REBUILD';
    }) {
      const response = await client.post('/maintenance/timesheet-financial-rebuild', data);
      return response.data as {
        project_id: number | null;
        rebuilt_weeks: string[];
        rebuilt_count: number;
      };
    },
  };
}

export type SettingsApi = ReturnType<typeof createSettingsApi>;
